import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, FormWarning } from "@shltelemedicine/shl-ui-lib";
import YearFormInput from "../../Shared/YearInput/YearFormInput";
import {
  CanceledOrderDetails,
  CreatedOrderDetails,
  IStudySubject,
  OrderFormDetails,
  OrderStatus,
} from "../../../models/order.interface";
import ConnectForm from "../../Shared/ConnectForm/ConnectForm";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import GenderSelectFormInput from "../GenderSelectFormInput/GenderSelectFormInput";
import VisitsSelectFormInput from "../VisitsSelectFormInput/VisitsSelectFormInput";
import clearIcon from "../../../assets/buttons/clear.svg";
import cancelIcon from "../../../assets/buttons/cancel.svg";
import styles from "./SubjectVisitInputs.module.css";
import OrderModal from "../OrderModal/OrderModal";
import { IStudy } from "../../../models/study.interface";
import studiesService from "../../../services/studies.service";
import useAlert from "../../../hooks/useAlert";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import ordersService from "../../../services/orders.service";
import moment from "moment";

type SubjectVisitInputsProps = {
  subject: IStudySubject;
  study: Partial<IStudy>;
  onReset: () => void;
};

const SubjectVisitInputs = ({
  subject: {
    id: subjectId,
    gender,
    yearOfBirth,
    nextVisitId,
    order,
    orderStatus,
  },
  study: { id: studyId, name: studyName, visits },
  onReset,
}: SubjectVisitInputsProps) => {
  const openOrder =
    orderStatus === OrderStatus.OpenOrderCancelable ||
    orderStatus === OrderStatus.OpenOrderNotCancelable;
  const newOrder =
    orderStatus === OrderStatus.NewOrderNewSubject ||
    orderStatus === OrderStatus.NewOrderExistingSubject;
  const disableSubjectDetailsInputs =
    openOrder ||
    orderStatus === OrderStatus.NewOrderExistingSubject ||
    !orderStatus;
  const disableVisitIdInput = openOrder || !orderStatus;

  const { handleSubmit, reset, getValues } = useFormContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [showCreated, setShowCreated] = useState<boolean>(false);
  const [showCanceled, setShowCanceled] = useState<boolean>(false);
  const [orderDetails, setOrderDetails] = useState<
    CreatedOrderDetails | CanceledOrderDetails
  >();

  const intl = useIntl();

  const {
    alertProps,
    alertMessage,
    showErrorAlert,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const yearOfBirthPlaceholder = intl.messages[
    "ORDERS.YEAR_OF_BIRTH.PLACEHOLDER"
  ] as string;

  const resetPageState = (r = {}) => {
    reset(r ?? {});
    onReset();
  };

  const clearHandler = () => resetPageState();

  const cancelHandler = async () => {
    try {
      setLoading(true);
      const { success, data, errorCode } = await ordersService.cancelOrder(
        order?.number ?? ""
      );

      if (!success) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.CANCEL_ORDER",
          "An error occurred while trying to delete an order.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      resetPageState({
        gender: undefined,
        yearOfBirth: undefined,
        visit: undefined,
      });
      setOrderDetails(data);
      setShowCanceled(true);
    } finally {
      setLoading(false);
    }
  };

  const submitHandler = async (orderForm: OrderFormDetails) => {
    setLoading(true);

    const {
      study: studyId,
      subjectId,
      gender,
      yearOfBirth,
      visitId,
    } = orderForm;

    try {
      const { success, data, errorCode } = await studiesService.createOrder(
        studyId,
        subjectId,
        gender,
        moment(yearOfBirth).year(),
        visitId
      );

      if (!success) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.ADD_ORDER",
          "An error occurred while adding an order.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      resetPageState();
      setOrderDetails(data);
      setShowCreated(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseOrderModal = () => {
    showCanceled && setShowCanceled(false);
    showCreated && setShowCreated(false);
  };

  useEffect(() => {
    const formValues = getValues();
    const year = yearOfBirth ? new Date(yearOfBirth, 1) : undefined;
    const visit =
      orderStatus === OrderStatus.NewOrderNewSubject
        ? visits
          ? visits[0]
          : undefined
        : visits?.find((visit) => visit.id === (order?.visitId ?? nextVisitId));

    reset({
      ...formValues,
      gender: gender,
      yearOfBirth: year,
      visitId: visit?.id,
    });
  }, [
    subjectId,
    gender,
    yearOfBirth,
    nextVisitId,
    getValues,
    reset,
    visits,
    order,
    orderStatus,
  ]);

  return (
    <>
      <ConnectForm<OrderFormDetails>>
        {({ control, formState: { errors: formErrors } }) => {
          return (
            <>
              <Card className="d-flex px-5 pt-4 muted-color app-shadow mx-auto">
                {orderStatus ? (
                  <div
                    className={`d-flex justify-content-between flex-md-row ${
                      newOrder ? `flex-row` : "flex-column"
                    }`}
                  >
                    {newOrder && (
                      <>
                        <span
                          className={`${styles.orderStatus} font-weight-bold`}
                        >
                          <FormattedMessage
                            id="ORDERS.NEW_ORDER"
                            defaultMessage="New Order"
                          />
                        </span>
                        {orderStatus === OrderStatus.NewOrderNewSubject && (
                          <span
                            className={`${styles.newSubjectSticker} d-flex align-self-end flex-column justify-content-center px-2 mx-3`}
                          >
                            <FormattedMessage
                              id="ORDERS.NEW_SUBJECT"
                              defaultMessage="New Subject"
                            />
                          </span>
                        )}
                      </>
                    )}
                    {openOrder && (
                      <>
                        <div className="d-flex align-items-start justify-content-between justify-content-md-start flex-wrap">
                          <span
                            className={`${styles.orderStatus} ${styles.openOrder} font-weight-bold mb-2 mb-sm-0`}
                          >
                            <FormattedMessage
                              id="ORDERS.OPEN_ORDER"
                              defaultMessage="Open Order"
                            />
                          </span>
                          <div className={`${styles.orderId} d-flex`}>
                            <span
                              className={`${styles.divider} d-flex flex-column justify-content-center mr-1`}
                            >
                              |
                            </span>
                            <span
                              className={`${styles.orderNumber} d-flex flex-column justify-content-center`}
                            >
                              {order?.number}
                            </span>
                          </div>
                        </div>
                        <div
                          className={`${styles.orderDetails} d-flex flex-column mt-2 m-sm-0 align-items-start align-items-sm-end`}
                        >
                          <span className="d-flex justify-content-end">
                            {studyName}
                          </span>
                          <span className="d-flex justify-content-end">
                            {studyId}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className={`${styles.orderStatus} mb-2 text-white`}>
                    placeholder
                  </div>
                )}
                <Form.Row className="flex-column align-items-md-end flex-md-row">
                  <Col xs="auto">
                    <Form.Label className="my-2 app-form-label">
                      <span className="prim-color">*</span>
                      <span className="mx-sm">
                        <FormattedMessage
                          id="ORDERS.GENDER"
                          defaultMessage="Gender"
                        />
                      </span>
                    </Form.Label>
                    <GenderSelectFormInput
                      required={true}
                      control={control}
                      disabled={loading || disableSubjectDetailsInputs}
                      isInvalid={!!formErrors?.gender}
                    />
                    {!!formErrors?.gender ? (
                      <FormWarning
                        className="font-sm flex-grow-1 mt-2"
                        message={
                          <FormattedMessage
                            id="ORDERS.GENDER_REQUIRED"
                            defaultMessage="Insert Gender"
                          />
                        }
                      />
                    ) : (
                      !!formErrors && (
                        <p
                          className={"text-center font-sm flex-grow-1"}
                          style={{ color: "white", visibility: "hidden" }}
                        >
                          Placeholder
                        </p>
                      )
                    )}
                  </Col>
                  <Col>
                    <Form.Label className="mb-2 app-form-label">
                      <span className="prim-color">*</span>
                      <span className="mx-sm">
                        <FormattedMessage
                          id="ORDERS.YEAR_OF_BIRTH"
                          defaultMessage="Year of Birth"
                        />
                      </span>
                    </Form.Label>
                    <YearFormInput
                      name={"yearOfBirth"}
                      control={control}
                      rules={{
                        required: true,
                      }}
                      input={{
                        min: new Date(1930, 0, 1),
                        max: new Date(2020, 11, 31),
                        placeholder: yearOfBirthPlaceholder,
                        isClearable: false,
                        isInvalid: !!formErrors?.yearOfBirth,
                        disabled: loading || disableSubjectDetailsInputs,
                      }}
                    />
                    {!!formErrors?.yearOfBirth ? (
                      <FormWarning
                        className="font-sm flex-grow-1 mt-2"
                        message={
                          <FormattedMessage
                            id="ORDERS.YEAR_OF_BIRTH_REQUIRED"
                            defaultMessage="Select Year"
                          />
                        }
                      />
                    ) : (
                      !!formErrors && (
                        <p
                          className={"text-center font-sm flex-grow-1"}
                          style={{ color: "white", visibility: "hidden" }}
                        >
                          Placeholder
                        </p>
                      )
                    )}
                  </Col>
                  <Col md={7}>
                    <Form.Label className="mb-2 app-form-label">
                      <span className="prim-color">*</span>
                      <span className="mx-sm">
                        <FormattedMessage
                          id="ORDERS.VISIT_ID"
                          defaultMessage="Visit ID"
                        />
                      </span>
                    </Form.Label>
                    <VisitsSelectFormInput
                      control={control}
                      required={true}
                      disabled={loading || disableVisitIdInput}
                      isInvalid={!!formErrors?.visitId}
                      items={visits ?? []}
                    />
                    {!!formErrors?.visitId ? (
                      <FormWarning
                        className="font-sm flex-grow-1 mt-2"
                        message={
                          <FormattedMessage
                            id="ORDERS.VISIT_ID_REQUIRED"
                            defaultMessage="Select Visit ID"
                          />
                        }
                      />
                    ) : (
                      !!formErrors && (
                        <p
                          className={"text-center font-sm flex-grow-1"}
                          style={{ color: "white", visibility: "hidden" }}
                        >
                          Placeholder
                        </p>
                      )
                    )}
                  </Col>
                </Form.Row>
                <div
                  className={`d-flex flex-wrap flex-column flex-md-nowrap flex-md-row align-items-md-end 
                  justify-content-${openOrder ? "between" : "end"} pt-1 pb-4`}
                >
                  {/* cancelation container */}
                  {openOrder && (
                    <div className="d-flex mb-3 mb-sm-0 flex-column flex-sm-row text-left">
                      {orderStatus === OrderStatus.OpenOrderCancelable && (
                        <Button
                          bgColor="transparent linear-gradient(180deg, #717689 0%, #393B45 100%) 0% 0% no-repeat padding-box"
                          className="align-self-start align-self-sm-end capitalized mr-2"
                          disabled={loading}
                          onClick={cancelHandler}
                          icon={{ src: cancelIcon }}
                        >
                          <FormattedMessage
                            id="ORDERS.CANCEL_ORDER"
                            defaultMessage="Cancel this order"
                          />
                        </Button>
                      )}
                      <span
                        className={`${styles.orderExistsMessage} d-flex flex-column justify-content-center m-2`}
                      >
                        <FormattedMessage
                          id={
                            orderStatus === OrderStatus.OpenOrderCancelable
                              ? "ORDERS.ORDER_PENDING_ECG_EXISTS"
                              : "ORDERS.ORDER_EXISTS"
                          }
                          defaultMessage={
                            orderStatus === OrderStatus.OpenOrderCancelable
                              ? "An open order pending ECG exists for the subject"
                              : "An open order already exists for this subject"
                          }
                        />
                      </span>
                    </div>
                  )}
                  {/*clear & submit buttons  */}
                  <div className="d-flex my-1 justify-content-end">
                    <Button
                      bgColor="transparent linear-gradient(180deg, #717689 0%, #393B45 100%) 0% 0% no-repeat padding-box"
                      className="align-self-end capitalized mr-2"
                      disabled={loading}
                      onClick={clearHandler}
                      icon={{ src: clearIcon }}
                    >
                      <FormattedMessage
                        id="ORDERS.CLEAR"
                        defaultMessage="Clear"
                      />
                    </Button>
                    <Button
                      bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
                      className="align-self-end capitalized"
                      disabled={loading || openOrder}
                      onClick={handleSubmit(submitHandler)}
                    >
                      <FormattedMessage
                        id="ORDERS.SUBMIT"
                        defaultMessage="Submit"
                      />
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Order created/canceled modal */}
              {orderDetails && (
                <OrderModal
                  mode={showCanceled ? "canceled" : "created"}
                  show={showCanceled || showCreated}
                  orderDetails={orderDetails ?? {}}
                  onClose={handleCloseOrderModal}
                />
              )}
            </>
          );
        }}
      </ConnectForm>
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};

export default SubjectVisitInputs;
